import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';

// The top of a section folds away while its list is scrolled DOWN, and comes back the moment it is scrolled UP
// (asked for 2026-11-03: on a phone the header, the search and the filter rows took half the screen).
//
// ONE provider in main.js watches every scroll inside the section (a capture listener on its root: a scroll event
// does not bubble, but it is captured) and the page's own scroll. A section marks what may fold away with <Retract>:
//  - collapse (default): for a section pinned to the screen whose list scrolls inside it (MIS, CRM, Supply ...). The
//    block's height goes to zero, so the list really gets the room.
//  - slide: for a bar that is sticky over a page that scrolls as a whole (Inventory, File Manager). Nothing moves in
//    the layout; the bar's sticky offset goes above the screen, so it scrolls away, and returns under the app bar.
// Every block of a section folds and returns together. It always shows within a few pixels of the top, when the
// scrolled list is gone (a drill-down on a phone, another tab) or stops being scrollable, while a field in it has the
// focus, and when the focus moves into it (Tab).

const RetractContext = createContext({ hidden: false, reveal: () => {} });

const HIDE_AFTER = 28;      // px scrolled down in one go before the top folds away
const SHOW_AFTER = 14;      // px scrolled up before it comes back
const TOP_ZONE = 8;         // within this of the top it is always shown
const QUIET_MS = 340;       // after a change the layout moves; the scroll events that causes are not the person's
export const RETRACT_MS = 220;
const APP_BAR = 60;

const isTyping = (root) => {
  const el = document.activeElement;
  return Boolean(el && root.contains(el) && el.closest('[data-retract]') && el.matches('input, textarea, [contenteditable="true"], [role="combobox"]'));
};
// what folding the collapsible blocks takes out of the layout: an inner list grows by that much
const collapsibleHeight = (root) => [...root.querySelectorAll('[data-retract="collapse"]')].reduce((sum, el) => sum + el.offsetHeight, 0);

export function ScrollRetractProvider({ children, resetKey }) {
  const rootRef = useRef(null);
  const [hidden, setHidden] = useState(false);
  const hiddenRef = useRef(false);
  const memo = useRef({ last: new WeakMap(), acc: 0, quietUntil: 0, target: null });

  const set = useCallback((value) => {
    if (hiddenRef.current === value) return;
    hiddenRef.current = value;
    memo.current.quietUntil = performance.now() + QUIET_MS;
    memo.current.acc = 0;
    setHidden(value);
  }, []);
  const reveal = useCallback(() => set(false), [set]);

  // another section: start with its top on screen
  useEffect(() => { set(false); }, [resetKey, set]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const m = memo.current;
    const onScroll = (event) => {
      const page = event.target === document || event.target === window;
      const el = page ? document.scrollingElement : event.target;
      if (!el || el.nodeType !== 1) return;
      // a field, a block that folds itself, or a scroller a screen opted out
      if (!page && (el.matches('textarea, input, [contenteditable="true"]') || el.closest('[data-retract], [data-retract-ignore]'))) return;
      const top = el.scrollTop;
      const prev = m.last.has(el) ? m.last.get(el) : top;
      m.last.set(el, top);
      m.target = el;
      if (top <= TOP_ZONE) { set(false); return; }
      if (performance.now() < m.quietUntil) return;
      const delta = top - prev;
      if (!delta) return;
      if ((delta > 0) !== (m.acc > 0)) m.acc = 0;
      m.acc += delta;
      if (!hiddenRef.current && m.acc >= HIDE_AFTER) {
        if (isTyping(root)) return;
        // only when there is clearly more to scroll than the top takes up, so folding it can never make the list fit
        // (which would bring the top straight back, and again, and again)
        const room = el.scrollHeight - el.clientHeight - (page ? 0 : collapsibleHeight(root));
        if (room > (page ? 120 : 48)) set(true);
      } else if (hiddenRef.current && -m.acc >= SHOW_AFTER) {
        set(false);
      }
    };
    root.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      root.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('scroll', onScroll);
    };
  }, [set]);

  // While folded: the list that was scrolled may be gone (a drill-down, another tab) or back at its top without an
  // event (it grew short); with nothing to scroll up, the top could never come back.
  useEffect(() => {
    if (!hidden) return undefined;
    const id = setInterval(() => {
      const el = memo.current.target;
      if (!el || !el.isConnected || el.scrollTop <= TOP_ZONE || el.scrollHeight - el.clientHeight <= TOP_ZONE) set(false);
    }, 300);
    return () => clearInterval(id);
  }, [hidden, set]);

  const value = useMemo(() => ({ hidden, reveal }), [hidden, reveal]);
  return (
    <RetractContext.Provider value={value}>
      {/* display: contents - the provider adds no box of its own to any layout */}
      <Box ref={rootRef} data-retract-root="" sx={{ display: 'contents' }}>{children}</Box>
    </RetractContext.Provider>
  );
}

export const useRetract = () => useContext(RetractContext);

// What may fold away. `slide` for a sticky bar over a page that scrolls as a whole; `disabled` keeps it on screen
// (File Manager while something is selected or waiting to be pasted).
export function Retract({ children, slide = false, disabled = false, sx, ...rest }) {
  const { hidden: folded, reveal } = useContext(RetractContext);
  const hidden = folded && !disabled;
  const ref = useRef(null);
  const [height, setHeight] = useState(0);
  // clip only while folded or moving: open, the block must not cut off what reaches outside it (a badge, a focus ring)
  const [clip, setClip] = useState(hidden);
  useEffect(() => {
    if (hidden) { setClip(true); return undefined; }
    const id = setTimeout(() => setClip(false), RETRACT_MS + 40);
    return () => clearTimeout(id);
  }, [hidden]);
  // a sliding bar goes exactly its own height above the app bar
  useEffect(() => { if (slide && hidden && ref.current) setHeight(ref.current.offsetHeight); }, [slide, hidden]);

  const motion = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } };
  if (slide) {
    return (
      <Box ref={ref} data-retract="slide" data-retracted={hidden ? 'true' : 'false'} onFocusCapture={reveal} {...rest}
        sx={{ position: 'sticky', zIndex: 9, top: hidden ? -(height + 2) : APP_BAR,
          transition: `top ${RETRACT_MS}ms ease`, ...motion, ...sx }}>
        {children}
      </Box>
    );
  }
  return (
    <Box ref={ref} data-retract="collapse" data-retracted={hidden ? 'true' : 'false'} onFocusCapture={reveal} {...rest}
      sx={{ display: 'grid', gridTemplateRows: hidden ? '0fr' : '1fr', flexShrink: 0,
        transition: `grid-template-rows ${RETRACT_MS}ms ease`, ...motion, ...sx }}>
      <Box sx={{ minHeight: 0, overflow: clip ? 'hidden' : 'visible', opacity: hidden ? 0 : 1,
        transition: `opacity ${RETRACT_MS}ms ease`, ...motion }}>
        {children}
      </Box>
    </Box>
  );
}
